import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody } from "@heroui/react";

interface ErrorPageProps {
    error?: Error;
    resetError?: () => void;
}

const ErrorPage = ({ error, resetError }: ErrorPageProps) => {
    const navigate = useNavigate();

    const handleRefresh = () => {
        if (resetError) {
            resetError();
        } else {
            window.location.reload();
        }
    };

    const handleGoHome = () => {
        const isAdminSite = import.meta.env.VITE_IS_ADMIN_SITE === 'true';
        navigate(isAdminSite ? '/dashboard' : '/');
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <Card className="max-w-md w-full shadow-none border border-border rounded">
                <CardBody className="text-center p-8">
                    <div className="flex justify-center mb-4">
                        <div className="p-3 rounded-full bg-danger/10">
                            <AlertTriangle className="h-12 w-12 text-danger" />
                        </div>
                    </div>

                    <h2 className="text-xl font-semibold text-foreground mb-2">
                        Something went wrong
                    </h2>

                    <p className="text-default-500 mb-6">
                        {error?.message || "An unexpected error occurred. Please try again or contact support if the problem persists."}
                    </p>

                    <div className="flex flex-col gap-2">
                        <Button
                            color="primary"
                            onPress={handleRefresh}
                            startContent={<RefreshCw className="h-4 w-4" />}
                            className="rounded"
                        >
                            Try Again
                        </Button>
                        <Button
                            variant="flat"
                            onPress={handleGoHome}
                            startContent={<Home className="h-4 w-4" />}
                            className="rounded"
                        >
                            Go Home
                        </Button>
                    </div>

                    {import.meta.env.DEV && error && (
                        <details className="mt-6 text-left">
                            <summary className="text-sm text-default-500 cursor-pointer hover:text-foreground">
                                Error Details (Development)
                            </summary>
                            <pre className="mt-2 text-xs text-default-400 bg-muted p-2 rounded overflow-auto">
                                {error.stack}
                            </pre>
                        </details>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default ErrorPage;